/**
 * RemoteEmergency — module-level store for the automation-driven tablet alarm
 * state.
 *
 * Flow (see EmergencyPage):
 *  1. Tablet emergency button → confirm → POSTs the `tabletToAutomation`
 *     webhook → shows "Alarm sent".
 *  2. The automation puts the tablet into ALARM MODE via an MQTT `emergency`
 *     command `{ state: 'alarm_mode', seconds }` → this store goes active and
 *     the tablet counts the given seconds down to 0 and freezes there.
 *  3. The automation later resets the tablet via `{ state: 'reset' }` → this
 *     store clears and the tablet returns to normal.
 *
 * Another device's alarm drives the SAME store on this tablet (the automation
 * targets the unit's residents), so a tablet can enter alarm mode without the
 * resident pressing anything.
 */

export interface RemoteEmergencyState {
  /** True while the tablet is in automation-driven alarm mode. */
  active: boolean;
  /** Countdown length (seconds) the tablet should count down from to 0. */
  seconds: number;
  /** Monotonic token bumped on each command — lets the page restart its timer. */
  epoch: number;
}

let state: RemoteEmergencyState = { active: false, seconds: 0, epoch: 0 };
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((l) => l());
}

/** Enter alarm mode with a countdown of `seconds` (clamped to >= 0). */
export function setAlarmMode(seconds: number | undefined | null): void {
  const secs = Math.max(0, Math.floor(Number(seconds) || 0));
  state = { active: true, seconds: secs, epoch: state.epoch + 1 };
  notify();
}

/** Clear alarm mode (automation 'reset', or local cancel). */
export function reset(): void {
  if (!state.active && state.seconds === 0) return;
  state = { active: false, seconds: 0, epoch: state.epoch + 1 };
  notify();
}

export function getState(): RemoteEmergencyState {
  return state;
}

export function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}
